import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, 
  Plus, 
  Users, 
  Settings, 
  Send, 
  Edit3, 
  Trash2, 
  ThumbsUp, 
  ThumbsDown,
  MoreVertical,
  Lock,
  Globe
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const Chat: React.FC = () => {
  const {
    conversations,
    currentConversation,
    messages,
    participants,
    userPermissions,
    isLoading,
    error,
    setCurrentConversation,
    createConversation,
    joinConversation,
    leaveConversation,
    sendMessage,
    editMessage,
    deleteMessage,
    reactToMessage,
    removeReaction,
    canAccessChat,
    canCreateConversations,
    canJoinConversations,
    canSendMessages,
    canEditMessages,
    canDeleteMessages,
    canReactToMessages
  } = useChat();

  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newConversationName, setNewConversationName] = useState('');
  const [newConversationDescription, setNewConversationDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll al final de los mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Verificar acceso al chat
  if (!canAccessChat) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">
            Acceso Restringido
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            No tienes permisos para acceder al chat. Contacta a un administrador.
          </p>
        </div>
      </div>
    );
  }

  // Manejar envío de mensaje
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentConversation) return;

    try {
      await sendMessage(newMessage.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // Manejar edición de mensaje
  const handleEditMessage = async () => {
    if (!editingMessage || !editText.trim()) return;

    try {
      await editMessage(editingMessage, editText.trim());
      setEditingMessage(null);
      setEditText('');
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  // Manejar eliminación de mensaje
  const handleDeleteMessage = async (messageId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este mensaje?')) return;

    try {
      await deleteMessage(messageId);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Manejar reacción a mensaje
  const handleReaction = async (messageId: string, reactionType: 'like' | 'dislike') => {
    try {
      await reactToMessage(messageId, reactionType);
    } catch (error) {
      console.error('Error reacting to message:', error);
    }
  };

  // Manejar creación de conversación
  const handleCreateConversation = async () => {
    if (!newConversationName.trim()) return;

    try {
      await createConversation(
        newConversationName.trim(),
        newConversationDescription.trim(),
        isPrivate
      );
      setShowCreateModal(false);
      setNewConversationName('');
      setNewConversationDescription('');
      setIsPrivate(false);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  // Manejar unirse a conversación
  const handleJoinConversation = async (conversationId: string) => {
    try {
      await joinConversation(conversationId);
      setCurrentConversation(conversations.find(c => c.id === conversationId) || null);
    } catch (error) {
      console.error('Error joining conversation:', error);
    }
  };

  // Manejar salir de conversación
  const handleLeaveConversation = async () => {
    if (!currentConversation) return;

    try {
      await leaveConversation(currentConversation.id);
    } catch (error) {
      console.error('Error leaving conversation:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Cargando chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <MessageCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">
            Error
          </h2>
          <p className="text-red-500 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar de conversaciones */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Header del sidebar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
              <MessageCircle className="h-6 w-6 mr-2 text-blue-600" />
              Chat
            </h1>
            {canCreateConversations && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                title="Crear nueva conversación"
              >
                <Plus className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Buscador */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar conversaciones..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Lista de conversaciones */}
        <div className="flex-1 overflow-y-auto">
          {conversations.map((conversation) => {
            const isCurrent = currentConversation?.id === conversation.id;
            const isParticipant = participants.some(p => p.conversation_id === conversation.id);
            
            return (
              <div
                key={conversation.id}
                className={`p-4 cursor-pointer transition-colors ${
                  isCurrent
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => setCurrentConversation(conversation)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {conversation.is_private ? (
                      <Lock className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Globe className="h-4 w-4 text-gray-500" />
                    )}
                    <h3 className={`font-medium ${
                      isCurrent ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'
                    }`}>
                      {conversation.name}
                    </h3>
                  </div>
                  {!isParticipant && canJoinConversations && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJoinConversation(conversation.id);
                      }}
                      className="px-2 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300 rounded-full hover:bg-green-200 dark:hover:bg-green-900/30 transition-colors"
                    >
                      Unirse
                    </button>
                  )}
                </div>
                
                {conversation.description && (
                  <p className={`text-sm ${
                    isCurrent ? 'text-blue-700 dark:text-blue-200' : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {conversation.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {conversation.participant_count || 0} participantes
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {format(new Date(conversation.updated_at), 'dd/MM HH:mm', { locale: es })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Área principal del chat */}
      <div className="flex-1 flex flex-col">
        {currentConversation ? (
          <>
            {/* Header de la conversación */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      {currentConversation.is_private && <Lock className="h-4 w-4 mr-2 text-gray-500" />}
                      {currentConversation.name}
                    </h2>
                    {currentConversation.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {currentConversation.description}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowParticipants(!showParticipants)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Ver participantes"
                  >
                    <Users className="h-5 w-5" />
                  </button>
                  
                  <button
                    onClick={handleLeaveConversation}
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    Salir
                  </button>
                </div>
              </div>
            </div>

            {/* Área de mensajes */}
            <div className="flex-1 flex">
              <div className="flex-1 flex flex-col">
                {/* Mensajes */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex space-x-3 ${
                        message.user_id === user?.id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {message.user_id !== user?.id && (
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {message.user?.email?.charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className={`max-w-xs lg:max-w-md ${
                        message.user_id === user?.id ? 'order-1' : 'order-2'
                      }`}>
                        {message.user_id !== user?.id && (
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {message.user?.email}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(message.created_at), 'HH:mm', { locale: es })}
                            </span>
                          </div>
                        )}
                        
                        <div className={`rounded-lg px-4 py-2 ${
                          message.user_id === user?.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                        }`}>
                          {editingMessage === message.id ? (
                            <div className="space-y-2">
                              <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                                rows={2}
                                autoFocus
                              />
                              <div className="flex space-x-2">
                                <button
                                  onClick={handleEditMessage}
                                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                >
                                  Guardar
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingMessage(null);
                                    setEditText('');
                                  }}
                                  className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="whitespace-pre-wrap">{message.message}</p>
                              {message.is_edited && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  (editado)
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Reacciones */}
                        <div className="flex items-center space-x-2 mt-2">
                          {canReactToMessages && (
                            <>
                              <button
                                onClick={() => handleReaction(message.id, 'like')}
                                className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs transition-colors ${
                                  message.reactions?.user_reaction === 'like'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-900/20 dark:hover:text-green-300'
                                }`}
                              >
                                <ThumbsUp className="h-3 w-3" />
                                <span>{message.reactions?.likes || 0}</span>
                              </button>
                              
                              <button
                                onClick={() => handleReaction(message.id, 'dislike')}
                                className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs transition-colors ${
                                  message.reactions?.user_reaction === 'dislike'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-300'
                                }`}
                              >
                                <ThumbsDown className="h-3 w-3" />
                                <span>{message.reactions?.dislikes || 0}</span>
                              </button>
                            </>
                          )}
                          
                          {/* Acciones del mensaje */}
                          {message.user_id === user?.id && (
                            <div className="flex items-center space-x-1">
                              {canEditMessages && (
                                <button
                                  onClick={() => {
                                    setEditingMessage(message.id);
                                    setEditText(message.message);
                                  }}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                                  title="Editar mensaje"
                                >
                                  <Edit3 className="h-3 w-3" />
                                </button>
                              )}
                              
                              {canDeleteMessages && (
                                <button
                                  onClick={() => handleDeleteMessage(message.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                                  title="Eliminar mensaje"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {message.user_id === user?.id && (
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user?.email?.charAt(0).toUpperCase() || 'T'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input de mensaje */}
                {canSendMessages && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex space-x-3">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Escribe un mensaje..."
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={!currentConversation}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || !currentConversation}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Panel de participantes */}
              {showParticipants && (
                <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Participantes
                  </h3>
                  
                  <div className="space-y-3">
                    {participants.map((participant) => (
                      <div
                        key={participant.id}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {participant.user?.email?.charAt(0).toUpperCase() || '?'}
                          </span>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {participant.user?.email}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {participant.user?.role} • {participant.role}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Estado sin conversación seleccionada */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">
                Selecciona una conversación
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                Elige una conversación del panel izquierdo para comenzar a chatear
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal para crear conversación */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Crear Nueva Conversación
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre
                </label>
                <input
                  type="text"
                  value={newConversationName}
                  onChange={(e) => setNewConversationName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nombre de la conversación"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descripción
                </label>
                <textarea
                  value={newConversationDescription}
                  onChange={(e) => setNewConversationDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Descripción opcional"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPrivate"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isPrivate" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Conversación privada
                </label>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateConversation}
                disabled={!newConversationName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
